import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from 'recharts';
import Spinner from '../components/ui/Spinner';
import Badge from '../components/ui/Badge';
import Pagination from '../components/ui/Pagination';
import { getBugReports, getBugReportStats } from '../services/bugReportService';
import { formatRelativeTime } from '../utils/dateUtils';

const STATUS_BADGE: Record<string, { variant: 'error' | 'warning' | 'success'; label: string }> = {
  OPEN: { variant: 'error', label: 'Open' },
  IN_PROGRESS: { variant: 'warning', label: '진행중' },
  RESOLVED: { variant: 'success', label: '해결' },
};

const CATEGORY_BADGE: Record<string, { variant: 'error' | 'info' | 'default'; label: string }> = {
  BUG: { variant: 'error', label: 'Bug' },
  FEATURE_REQUEST: { variant: 'info', label: 'FR' },
  QUESTION: { variant: 'default', label: '질문' },
};

const CAT_COLORS: Record<string, string> = { BUG: '#EF4444', FEATURE_REQUEST: '#3B82F6', QUESTION: '#9CA3AF' };
const CAT_LABELS: Record<string, string> = { BUG: 'Bug', FEATURE_REQUEST: '기능 요청', QUESTION: '질문' };

const PERIOD_OPTIONS = [
  { value: 7, label: '7일' }, { value: 30, label: '30일' }, { value: 90, label: '90일' },
];

export default function BugReportsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') ?? 'list';
  const initialOrgId = searchParams.get('organizationId') ?? '';

  const setTab = (t: string) => {
    const p = new URLSearchParams(searchParams);
    if (t === 'list') p.delete('tab'); else p.set('tab', t);
    setSearchParams(p);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">오류 보고</h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {[{ key: 'list', label: '목록' }, { key: 'stats', label: '통계' }].map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
              tab === t.key ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>{t.label}</button>
        ))}
      </div>

      {tab === 'list' ? <ListTab navigate={navigate} initialOrgId={initialOrgId} /> : <StatsTab navigate={navigate} />}
    </div>
  );
}

// ====== 목록 탭 ======
function ListTab({ navigate, initialOrgId }: { navigate: ReturnType<typeof useNavigate>; initialOrgId: string }) {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [category, setCategory] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'bug-reports', { page, search, status, category, organizationId: initialOrgId }],
    queryFn: () => getBugReports({ page, limit: 20, search: search || undefined, status: status || undefined, category: category || undefined, organizationId: initialOrgId || undefined }),
  });

  return (
    <>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400" />
          <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="제목 검색"
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500" />
        </div>
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-1 focus:ring-primary-500">
          <option value="">전체 상태</option><option value="OPEN">Open</option><option value="IN_PROGRESS">진행중</option><option value="RESOLVED">해결</option>
        </select>
        <select value={category} onChange={(e) => { setCategory(e.target.value); setPage(1); }}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-1 focus:ring-primary-500">
          <option value="">전체 카테고리</option><option value="BUG">Bug</option><option value="FEATURE_REQUEST">기능 요청</option><option value="QUESTION">질문</option>
        </select>
      </div>

      {isLoading ? <div className="flex justify-center py-16"><Spinner className="size-8" /></div> : (
        <>
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead><tr className="border-b border-gray-200 bg-gray-50">
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase w-16">ID</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase">조직</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase">카테고리</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase">제목</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase">상태</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase">접수일</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-100">
                {data?.items.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-sm text-gray-400">오류 보고가 없습니다.</td></tr>
                ) : data?.items.map((r) => {
                  const st = STATUS_BADGE[r.status] ?? STATUS_BADGE.OPEN;
                  const cat = CATEGORY_BADGE[r.category] ?? CATEGORY_BADGE.BUG;
                  return (
                    <tr key={r.id} onClick={() => navigate(`/bug-reports/${r.id}`)} className="hover:bg-gray-50 cursor-pointer">
                      <td className="px-4 py-3 text-sm text-gray-400 font-mono">#{r.reportNumber}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{r.organizationName ?? '-'}</td>
                      <td className="px-4 py-3"><Badge variant={cat.variant}>{cat.label}</Badge></td>
                      <td className="px-4 py-3 text-sm text-gray-900 font-medium max-w-[250px] truncate">{r.title}</td>
                      <td className="px-4 py-3"><Badge variant={st.variant}>{st.label}</Badge></td>
                      <td className="px-4 py-3 text-sm text-gray-500">{formatRelativeTime(r.createdAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {data && <Pagination page={data.page} totalPages={data.totalPages} onPageChange={setPage} />}
        </>
      )}
    </>
  );
}

// ====== 통계 탭 ======
function StatsTab({ navigate }: { navigate: ReturnType<typeof useNavigate> }) {
  const [days, setDays] = useState(30);

  const { data: stats, isLoading } = useQuery({
    queryKey: ['admin', 'bug-reports', 'stats', days],
    queryFn: () => getBugReportStats(days),
  });

  if (isLoading) return <div className="flex justify-center py-16"><Spinner className="size-8" /></div>;
  if (!stats) return null;

  const totalCount = stats.categoryDistribution.reduce((s, c) => s + c.count, 0);
  const pieData = stats.categoryDistribution.map((c) => ({ name: CAT_LABELS[c.category] ?? c.category, value: c.count, category: c.category }));
  const trendData = stats.weeklyTrend.labels.map((l, i) => ({ label: l, count: stats.weeklyTrend.data[i] ?? 0 }));
  const orgData = stats.organizationRanking.map((o) => ({ name: o.organizationName, count: o.count, id: o.organizationId }));

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {PERIOD_OPTIONS.map((opt) => (
            <button key={opt.value} onClick={() => setDays(opt.value)}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${days === opt.value ? 'bg-gray-900 text-white' : 'text-gray-600'}`}>{opt.label}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* 카테고리별 분포 */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-medium text-gray-700 mb-4">카테고리별 분포</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={2}>
                {pieData.map((e) => <Cell key={e.category} fill={CAT_COLORS[e.category] ?? '#9CA3AF'} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-4 mt-2">
            {pieData.map((d) => (
              <span key={d.category} className="flex items-center gap-1.5 text-sm text-gray-500">
                <span className="size-2.5 rounded-full" style={{ background: CAT_COLORS[d.category] }} />
                {d.name}: {d.value} ({totalCount > 0 ? Math.round(d.value / totalCount * 100) : 0}%)
              </span>
            ))}
          </div>
        </div>

        {/* 주간 접수 추이 */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-medium text-gray-700 mb-4">주간 접수 추이</h3>
          <ResponsiveContainer width="100%" height={256}>
            <BarChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" name="접수 건수" fill="#6366F1" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* 조직별 Top 10 */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-medium text-gray-700 mb-4">조직별 오류 건수 Top 10</h3>
          {orgData.length === 0 ? <p className="text-center text-sm text-gray-400 py-8">데이터가 없습니다.</p> : (
            <ResponsiveContainer width="100%" height={256}>
              <BarChart data={orgData} layout="vertical" margin={{ left: 80 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={70}
                  onClick={(_: unknown, idx: number) => { if (orgData[idx]?.id) navigate(`/organizations/${orgData[idx].id}`); }} />
                <Tooltip />
                <Bar dataKey="count" name="오류 건수" fill="#F59E0B" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* 평균 해결 소요 시간 */}
        <div className="bg-white rounded-xl border border-gray-200 p-8 flex flex-col items-center justify-center">
          <h3 className="text-sm font-medium text-gray-700 mb-6">평균 해결 소요 시간</h3>
          {stats.avgResolutionHours !== null ? (
            <>
              <p className="text-4xl font-bold text-gray-900">{stats.avgResolutionHours}시간</p>
              <p className="text-lg text-gray-500 mt-1">(약 {(stats.avgResolutionHours / 24).toFixed(1)}일)</p>
            </>
          ) : (
            <p className="text-sm text-gray-400">해결된 건이 없습니다.</p>
          )}
        </div>
      </div>
    </div>
  );
}
